/*
 * The Dog Collection — homepage transparent header driver.
 *
 * The reference keeps the homepage header fixed the whole time: transparent over the
 * hero (white text), then it frosts to solid paper (ink text) as you scroll — exactly
 * `scrolled = window.scrollY > 12`. Horizon has this behaviour built in via
 * data-sticky-state, but it renders the transparent header as position:absolute so it
 * scrolls away; we pin it with position:fixed instead. That pinning defeats Horizon's
 * IntersectionObserver (it watches the header at threshold 1, which a viewport-fixed
 * header can never re-satisfy against the scroll-container root — it gets stuck
 * "active"). So we own the state here: toggle a .db-scrolled class past a 12px
 * threshold and let CSS (keyed on that class, #id specificity) drive the look.
 *
 * Only the homepage header carries [transparent]; every other page is untouched.
 */
(function () {
  var THRESHOLD = 12; // reference: window.scrollY > 12

  function init() {
    // The homepage header carries [transparent]; the product header carries
    // [data-db-mobile-transparent] (its transparent overlay is mobile-only, driven by
    // CSS in dogbook.css). Both want the same .db-scrolled toggle past a 12px scroll.
    var header = document.querySelector(
      '#header-component[transparent], #header-component[data-db-mobile-transparent]'
    );
    if (!header) return; // neither homepage nor product — nothing to do

    // The fixed transparent header spans the full viewport, but the page scrolls inside
    // .page-wrapper, whose scrollbar narrows the visible content. Publish that scrollbar
    // width so the header can centre its content on the VISIBLE area (dogbook.css) instead
    // of the full viewport, keeping the wordmark aligned with the page content below it —
    // otherwise the centred logo drifts toward the scrollbar. (The Lovable reference does
    // the same, via its document scrollbar.) 0 on overlay-scrollbar systems → no shift.
    function updateScrollbar() {
      var pw = document.querySelector('.page-wrapper');
      var sbw = pw ? pw.offsetWidth - pw.clientWidth : 0;
      document.documentElement.style.setProperty('--db-content-sbw', sbw + 'px');
    }
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar, { passive: true });

    var ticking = false;

    // Horizon scrolls .page-wrapper on desktop and the document on mobile, and which
    // one is scrollable can change after load. Rather than guess, we listen to both
    // and read whichever has actually moved — the max of the two scroll positions.
    function scrollTop() {
      var pw = document.querySelector('.page-wrapper');
      var docTop = window.scrollY || document.documentElement.scrollTop || 0;
      var pwTop = pw ? pw.scrollTop : 0;
      return Math.max(docTop, pwTop);
    }

    function apply() {
      ticking = false;
      // .db-scrolled is our single source of truth for the header state. (Horizon's own
      // data-sticky-state can't be trusted here: its IntersectionObserver gets stuck
      // "active" once we pin the header with position:fixed, and it asynchronously
      // overrides anything we set. So the CSS keys off this class instead, with
      // !important, independent of whatever state Horizon settles on.)
      header.classList.toggle('db-scrolled', scrollTop() > THRESHOLD);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      (window.requestAnimationFrame || window.setTimeout)(apply, 0);
    }

    // Attach to both candidates (capture phase catches the page-wrapper's scroll even
    // if it isn't the event target we'd have guessed).
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    var pw = document.querySelector('.page-wrapper');
    if (pw) pw.addEventListener('scroll', onScroll, { passive: true });

    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
